import React, { useState } from 'react';
import { emptyRouteRow, parseRoutePaste } from '../utils/programRoutes';

/**
 * Table editor for a Marketo destination's per-lead program routing
 * (target_config.program_routes — backend app/utils/delivery/program_routes.py):
 * which Marketo program each lead goes to, decided by the value of one
 * destination field (Uber Restaurants: cSContentTitle, one program per asset).
 *
 * Presentational: the page owns the state and does the Marketo lookup.
 * Props:
 *   field, onFieldChange(value)        the destination field to route on
 *   fieldOptions                       suggestions for it (the destination's own mapped fields)
 *   rows, onRowsChange(updaterOrRows)  rows are {title, aliases, programId,
 *                                      programName, programStatus} strings; this is
 *                                      called with an updater function (pass a React
 *                                      setState) so overlapping async lookups can't
 *                                      overwrite each other
 *   onLookup(programId) => Promise<name>   resolves a Marketo program id to its name
 *                                          (leads/push.json wants the name, a client gives ids)
 */
const labelStyle = { fontSize: '11px', color: '#888', fontWeight: '600', marginBottom: '3px', display: 'block' };
const inputStyle = {
  padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const smallBtn = (color, disabled) => ({
  padding: '5px 10px', background: disabled ? '#bdc3c7' : color, color: 'white', border: 'none',
  borderRadius: '4px', cursor: disabled ? 'default' : 'pointer', fontWeight: '600', fontSize: '12px',
});
const GRID = '1.6fr 1.2fr 0.7fr 1.6fr auto';

export default function ProgramRoutesEditor({ field, onFieldChange, fieldOptions, rows, onRowsChange, onLookup }) {
  const [pasteText, setPasteText] = useState('');
  const [pasteMsg, setPasteMsg] = useState('');
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});

  const update = (idx, patch) => onRowsChange((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx) => {
    onRowsChange((prev) => prev.filter((_, i) => i !== idx));
    setBusy({});
    setErrors({});
  };

  const lookup = async (idx) => {
    const id = String((rows[idx] && rows[idx].programId) || '').trim();
    if (!/^\d+$/.test(id)) {
      setErrors((e) => ({ ...e, [idx]: 'Enter the numeric program ID first.' }));
      return;
    }
    setBusy((b) => ({ ...b, [idx]: true }));
    setErrors((e) => ({ ...e, [idx]: '' }));
    try {
      const name = await onLookup(id);
      onRowsChange((prev) => prev.map((r, i) => (i === idx ? { ...r, programName: name } : r)));
    } catch (err) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      setErrors((e) => ({ ...e, [idx]: detail || (err && err.message) || 'Lookup failed.' }));
    } finally {
      setBusy((b) => ({ ...b, [idx]: false }));
    }
  };

  const lookupAll = async () => {
    for (let i = 0; i < rows.length; i += 1) {
      if (/^\d+$/.test(String(rows[i].programId || '').trim()) && !rows[i].programName) {
        await lookup(i);
      }
    }
  };

  const addPasted = () => {
    const parsed = parseRoutePaste(pasteText);
    if (!parsed.length) {
      setPasteMsg('Nothing to add — paste the asset title and program ID columns.');
      return;
    }
    onRowsChange((prev) => [
      ...prev.filter((r) => r.title.trim() || r.programId.trim() || r.programName.trim()),
      ...parsed.map((p) => ({ ...emptyRouteRow(), title: p.title, programId: p.programId })),
    ]);
    setPasteText('');
    setPasteMsg(`Added ${parsed.length} route${parsed.length !== 1 ? 's' : ''} — click "Look up all names" to fill in the program names.`);
  };

  const needsLookup = rows.some((r) => /^\d+$/.test(String(r.programId || '').trim()) && !r.programName);

  return (
    <div>
      <div style={{ fontSize: '12px', color: '#777', marginBottom: '10px' }}>
        Each lead goes to the program matching its value in the field below, and that row&apos;s title is what Marketo
        receives in the field. Case, punctuation and spacing are ignored when matching; add other spellings under
        &quot;Also accept&quot;. A lead whose value matches no row isn&apos;t sent. With routes set, the single Program Name above is not used.
      </div>

      <div style={{ maxWidth: '280px', marginBottom: '10px' }}>
        <label style={labelStyle}>Route on this destination field</label>
        <input
          style={inputStyle}
          value={field}
          onChange={(e) => onFieldChange(e.target.value)}
          list="program-route-field-options"
          placeholder="the destination field that holds the value"
        />
        <datalist id="program-route-field-options">
          {(fieldOptions || []).map((o) => <option key={o} value={o} />)}
        </datalist>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginBottom: '4px' }}>
          No routes yet — click &quot;+ Add route&quot;, or paste the client&apos;s table below.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '8px', marginBottom: '4px' }}>
          <span style={labelStyle}>Value (as the client lists it)</span>
          <span style={labelStyle}>Also accept (comma-separated)</span>
          <span style={labelStyle}>Program ID</span>
          <span style={labelStyle}>Marketo program name</span>
          <span />
        </div>
      )}
      {rows.map((r, idx) => (
        <div key={idx} style={{ marginBottom: '6px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '8px', alignItems: 'center' }}>
            <input style={inputStyle} value={r.title} onChange={(e) => update(idx, { title: e.target.value })} placeholder="e.g. the asset title" />
            <input style={inputStyle} value={r.aliases} onChange={(e) => update(idx, { aliases: e.target.value })} placeholder="other spellings" />
            <input style={inputStyle} value={r.programId} onChange={(e) => update(idx, { programId: e.target.value, programName: '' })} placeholder="Marketo ID" />
            <div style={{ display: 'flex', gap: '6px' }}>
              <input style={inputStyle} value={r.programName} onChange={(e) => update(idx, { programName: e.target.value })} placeholder="filled by Look up" />
              <button type="button" style={smallBtn('#1a6da8', !!busy[idx])} disabled={!!busy[idx]} onClick={() => lookup(idx)}>
                {busy[idx] ? '…' : 'Look up'}
              </button>
            </div>
            <button type="button" style={smallBtn('#c0392b', false)} onClick={() => removeRow(idx)} title="Remove this route">×</button>
          </div>
          {errors[idx] && <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '3px' }}>{errors[idx]}</div>}
        </div>
      ))}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <button type="button" style={smallBtn('#27ae60', false)} onClick={() => onRowsChange((prev) => [...prev, emptyRouteRow()])}>+ Add route</button>
        <button type="button" style={smallBtn('#8e44ad', !needsLookup)} disabled={!needsLookup} onClick={lookupAll}>Look up all names</button>
      </div>

      <div style={{ marginTop: '12px' }}>
        <label style={labelStyle}>Paste the client&apos;s table (value and program ID)</label>
        <textarea
          style={{ ...inputStyle, minHeight: '64px', fontFamily: 'inherit' }}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder={'One route per line: value, then program ID (tab-separated, e.g. from a spreadsheet)'}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
          <button type="button" style={smallBtn('#2c3e50', !pasteText.trim())} disabled={!pasteText.trim()} onClick={addPasted}>Add pasted routes</button>
          {pasteMsg && <span style={{ fontSize: '12px', color: '#555' }}>{pasteMsg}</span>}
        </div>
      </div>
    </div>
  );
}
